import { type Manifest, PropertyName, v4 } from '@sap/ux-specification-types';
import { SchemaTag } from '@sap/ux-specification-types';
import type { ToolBar } from './ToolBar';
import { addPatternForBindingChangeOfEnumLR, doNotDelete } from '../../../common';
import { descriptionSrcURL, syncRule, validity, hide, message } from '../../../common/decoration/decorators';
import { when, and, msg, not, exists, tableContext, or } from '../../../common/decoration/decorator-paths';
import {
    getTableSettingsPathForLR,
    getSettingsPathForPage,
    getSettingsPathForLRViewsPaths,
    getTableSettingsPathForView,
    getQuickVariantPath,
    getTableAnalyticalConfigurationPathForLR,
    getTableAnalyticalConfigurationForView
} from '../../application';
import { FIELD_SEPARATOR, QUALIFIER_SEPARATOR } from '../../utils/utils';
import type { PopinLayout } from 'sap/m/library';

/**
 * Deletes a property from the creation mode section of the manifest object,
 * and removes the creation mode section if it becomes empty.
 *
 * @param {unknown} manifestSection - The section of the manifest object containing the creation mode property.
 * @param {object} _configObject - Configuration object (not currently used in the method logic).
 * @param {string} _key - The specific key (not currently used in the method logic).
 * @param {string} manifestKey - The key identifying the property in the creation mode to be deleted.
 */
export function deleteCreationModeProperty(
    manifestSection: unknown,
    _configObject: object,
    _key: string,
    manifestKey: string
): void {
    if (
        typeof manifestSection[PropertyName.creationMode] === 'object' &&
        manifestSection[PropertyName.creationMode][manifestKey] !== undefined
    ) {
        delete manifestSection[PropertyName.creationMode][manifestKey];
        if (Object.keys(manifestSection[PropertyName.creationMode]).length === 0) {
            delete manifestSection[PropertyName.creationMode];
        }
    }
}

/**
 * Exports the creation mode property from a given configuration object to a manifest section.
 *
 * @param {unknown} manifestSection - The section of the manifest where the creation mode property will be updated.
 * @param {unknown} configObject - The configuration object containing the creation mode property.
 * @param {string} key - The key used to identify the specific property within the creation mode section.
 */
export function exportCreationModeProperty(manifestSection: unknown, configObject: unknown, key: string): void {
    if (manifestSection[PropertyName.creationMode] && typeof manifestSection[PropertyName.creationMode] === 'object') {
        manifestSection[PropertyName.creationMode][key] = configObject[key];
    }
}

/**
 * Updates the 'creationMode.creationFields' property in the manifestSection object
 * by joining the 'creationFields' array from the configObject into a string separated by commas.
 *
 * @param {object} manifestSection - The object where the 'creationMode.creationFields' property will be updated.
 * @param {object} configObject - The object containing the 'creationFields' array to be used for the update.
 */
export function exportCreationModeCreationFieldsProperty(manifestSection: unknown, configObject: unknown): void {
    if (manifestSection[PropertyName.creationMode]) {
        const creationFields = configObject[PropertyName.creationFields] ?? [];
        if (creationFields.length > 0) {
            manifestSection[PropertyName.creationMode][PropertyName.creationFields] = creationFields
                .map((field) => field.replace(FIELD_SEPARATOR, QUALIFIER_SEPARATOR))
                .join(',');
        } else {
            delete manifestSection[PropertyName.creationMode][PropertyName.creationFields];
        }
    }
}

/**
 * Extracts and processes the `creationFields` property from the provided manifest section.
 *
 * @param {object[]} manifestSection - An array of objects representing the manifest section,
 * where the `creationFields` property might exist.
 * @returns {string[] | undefined} An array of strings representing the split values of the `creationFields`
 * property if it exists and is a valid string, otherwise `undefined`.
 */
export function importCreationModeCreationFieldsProperty(manifestSection: object[]): string[] | undefined {
    const creationFields = manifestSection?.[PropertyName.creationFields];
    if (creationFields !== undefined && typeof creationFields === 'string') {
        return creationFields === ''
            ? []
            : creationFields.split(',').map((field) => field.replace(QUALIFIER_SEPARATOR, FIELD_SEPARATOR));
    }
    return undefined;
}

/**
 * Sync rule for importing personalization. This function performs an implicit conversion of a former boolean value to the new object representation.
 *
 * @param tableSettings - table settings in manifest, to be analyzed
 * @returns - object representing the value(s) for the config file
 */
export function importCreationMode(tableSettings: object): object | undefined {
    const value: Record<string, unknown> = structuredClone(tableSettings?.[PropertyName.creationMode]);
    const creationFields = value?.[PropertyName.creationFields];
    if (creationFields !== undefined && typeof creationFields === 'string') {
        value[PropertyName.creationFields] =
            creationFields === ''
                ? []
                : creationFields.split(',').map((field) => field.replace(QUALIFIER_SEPARATOR, FIELD_SEPARATOR));
    }
    return value;
}

/**
 * Special edition of the global function getManifestSectionByPathV4 that initializes the views array if empty.
 *
 * @param manifest - manifest.json
 * @param path - path to the views array in manifest
 * @returns the paths array of the views
 */
const getManifestSectionByPathV4 = (manifest: object, path: string): Array<object> => {
    let manifestSection = manifest;
    for (const element of path.split('/')) {
        if (!manifestSection[element]) {
            if (element === 'paths') {
                manifestSection[element] = [];
            } else {
                manifestSection[element] = {};
            }
        }
        manifestSection = manifestSection[element];
    }
    return manifestSection as Array<object>;
};

/**
 * Common function for determining the path in manifest of a list report view's path.
 *
 * @param pageName - page name in manifest
 * @param manifest - manifest.json
 * @param _targetAnnotationEncoded - target annotation (not used here)
 * @param viewKey - value of the key property of the view
 * @param _targetAnnotation - target annotation used for additional configuration
 * @param configObject - configuration object containing key-value pairs for the view
 * @returns the path to the entry in manifest
 */
function getSettingsPathForLRViewsPath(
    pageName: string[],
    manifest: Manifest,
    _targetAnnotationEncoded: string,
    viewKey: string,
    _targetAnnotation: string,
    configObject: object
): string {
    const viewsPath = getSettingsPathForLRViewsPaths(pageName);
    const manifestSection = getManifestSectionByPathV4(JSON.parse(JSON.stringify(manifest)), viewsPath);
    let path: string;
    for (let index = 0; index < manifestSection.length; index++) {
        const view = manifestSection[index];
        if (view && (view[SchemaTag.key] === configObject?.['key'] || view[SchemaTag.key] === viewKey)) {
            path = `${viewsPath}/[]/${index}`;
            break;
        }
    }
    if (!path) {
        path = `${viewsPath}/[]`;
    }
    return path;
}

/**
 * Subroutine of checkViewOrder that creates a new list of views for the manifest with updated order.
 *
 * @param manifestSection - The section of the manifest containing the views.
 * @param keyInConfig - The key of the view in the configuration object.
 * @param newIndex - The new index for the view in the manifest.
 * @param minIndex - The minimum index of the range to be reordered.
 * @param maxIndex - The maximum index of the range to be reordered.
 * @param direction - The direction of reordering, where -1 indicates moving down and 1 indicates moving up.
 * @returns A new array of objects representing the reordered views.
 */
function fillNewlyOrderedViews(
    manifestSection: object,
    keyInConfig: string,
    newIndex: string,
    minIndex: number,
    maxIndex: number,
    direction: number
): Array<object> {
    const newlyOrderedViews = JSON.parse(JSON.stringify(manifestSection));
    const viewKeys = Object.keys(manifestSection);

    for (const index of viewKeys) {
        const indexInManifest = Number(index);
        const view = manifestSection[index];
        if (keyInConfig === view['key']) {
            newlyOrderedViews[newIndex] = view;
        } else {
            if (indexInManifest >= minIndex && indexInManifest <= maxIndex) {
                newlyOrderedViews[Math.max(0, indexInManifest + direction)] = view;
            } else {
                newlyOrderedViews[index] = view;
            }
        }
    }
    return newlyOrderedViews;
}

/**
 * If a view index was changed we need to adapt the order of views in manifest.
 *
 * @param manifestSection - section in manifest comprising the table settings
 * @param configObject - section in config.json describing the table settings
 */
export function checkViewOrder(manifestSection: unknown, configObject: unknown): void {
    const keyInConfig = configObject['key'];
    const oldIndex = (manifestSection as Array<object>).findIndex((view) => {
        return view?.['key'] === keyInConfig;
    });
    const newIndex = configObject['index'];
    if (oldIndex > -1 && oldIndex !== newIndex) {
        const direction = newIndex > oldIndex ? -1 : 1;
        const minIndex = Math.min(Number(oldIndex), newIndex);
        const maxIndex = Math.max(Number(oldIndex), newIndex);
        const newlyOrderedViews = fillNewlyOrderedViews(
            manifestSection as object,
            keyInConfig,
            newIndex,
            minIndex,
            maxIndex,
            direction
        );
        for (const index of Object.keys(newlyOrderedViews)) {
            manifestSection[index] = newlyOrderedViews[index];
        }
    }
}

/**
 * Common sync rule function for export of creation for personalization object.
 *
 * @param manifestSection - section in manifest comprising the table settings
 * @param configObject - section in config.json describing the table settings
 * @param key - key or property in config.json
 * @param manifestKey - key or property in manifest
 */
export function exportPersonalization(
    manifestSection: unknown,
    configObject: unknown,
    key: string,
    manifestKey?: string
): void {
    const value = configObject[key];
    const oldValue = manifestSection[manifestKey];
    if (typeof value === 'object') {
        if (!Object.keys(value).length && typeof oldValue === 'object' && Object.keys(oldValue).length) {
            // Delete property
            delete manifestSection[manifestKey];
        } else if (Object.keys(value).length) {
            manifestSection[manifestKey] = value;
        }
    } else if (typeof value === 'boolean') {
        manifestSection[manifestKey] = value;
    }
}

/**
 * Common sync rule function for export of personalization properties.
 *
 * @param manifestSection - section in manifest comprising the table settings
 * @param configObject - section in config.json describing the table settings
 * @param key - key or property in config.json
 * @param manifestKey - key or property in manifest
 */
export function exportPersonalizationProperty(
    manifestSection: unknown,
    configObject: unknown,
    key: string,
    manifestKey: string
): void {
    if (typeof manifestSection['personalization'] === 'boolean' || !manifestSection['personalization']) {
        manifestSection['personalization'] = {};
    }
    manifestSection['personalization'][manifestKey] = configObject[key];
}

/**
 * Common sync rule function for the deletion of any property of personalization.
 *
 * @param manifestSection - section in manifest comprising the table settings
 * @param _configObject - page config object
 * @param _key - key or property in page config object
 * @param manifestKey - key or property in manifest
 */
export function deletePersonalizationProperty(
    manifestSection: unknown,
    _configObject: object,
    _key: string,
    manifestKey: string
): void {
    if (
        typeof manifestSection['personalization'] === 'object' &&
        manifestSection['personalization'][manifestKey] !== undefined
    ) {
        delete manifestSection['personalization'][manifestKey];
        if (Object.keys(manifestSection['personalization']).length === 0) {
            delete manifestSection['personalization'];
        }
    } else if (
        typeof manifestSection['personalization'] === 'string' &&
        manifestSection['personalization'] !== undefined
    ) {
        delete manifestSection['personalization'];
    }
}

/**
 * Sync rule deleting a view if the index gets deleted.
 *
 * @param manifestSection - manifest section of the given view
 * @param configObject - section of the configuration JSON
 * @param _configPropertyKey - The key of the property in the configuration object (not used here)
 * @param _manifestKey - The key or property in the manifest (not used here)
 * @param syncRuleKey - key of the view
 * @param config - the complete config JSON
 */
function deleteView(
    manifestSection: object,
    configObject: object,
    _configPropertyKey: string,
    _manifestKey: string,
    syncRuleKey: string,
    config: object
): void {
    let deletedViewIndex;
    if (!configObject || Object.keys(configObject).length === 0) {
        const viewsInManifest = manifestSection as Array<object>;
        Object.keys(viewsInManifest).forEach((index) => {
            if (viewsInManifest[index] && viewsInManifest[index].key === syncRuleKey) {
                manifestSection[Number(index)] = {};
                deletedViewIndex = index;
            }
        });
    }
    if (deletedViewIndex) {
        const viewsInConfig = config['table']?.['views'];
        for (const key in viewsInConfig) {
            if (viewsInConfig[key].index > deletedViewIndex) {
                viewsInConfig[key].index--;
            }
        }
    }
}

/**
 * Sync rule for the import of the index of a table view.
 *
 * @param manifestSection - manifest section, as determined by synRule.path
 * @param _sectionId  - not used
 * @param _propertyKey - not used
 * @param breadcrumbs - list if breadcrumbs, includes the given view key
 * @returns index of the view in manifest, undefined if not found
 */
function determineIndexOfView(
    manifestSection: object[],
    _sectionId: string,
    _propertyKey: string,
    breadcrumbs: string[]
): number | undefined {
    const viewIndex = manifestSection.findIndex((view) => view['key'] === breadcrumbs[0]);
    return viewIndex === -1 ? undefined : viewIndex;
}

/**
 * Imports enableMassEdit settings from the manifest section.
 *
 * @param manifestSection - The section of the manifest containing multi-edit settings.
 * @returns The enableMassEdit manifest boolean/object or undefined if not present.
 */
export function importEnableMassEdit(manifestSection) {
    if (!manifestSection || manifestSection['enableMassEdit'] === undefined) {
        return undefined;
    }
    const manifestPart: v4.EnableMassEdit = JSON.parse(JSON.stringify(manifestSection['enableMassEdit']));

    if (typeof manifestPart === 'boolean' || Object.keys(manifestPart).length === 0) {
        return manifestPart;
    }
    // convert CSV format to array of strings
    if (manifestPart.visibleFields !== undefined) {
        manifestPart.visibleFields = manifestSection['enableMassEdit']?.['visibleFields']?.split(',');
    }
    if (manifestPart.ignoredFields !== undefined) {
        manifestPart.ignoredFields = manifestSection['enableMassEdit']?.['ignoredFields']?.split(',');
    }
    return manifestPart;
}

/**
 * Exports enableMassEdit settings from the config.
 *
 * @param manifestSection - The section of the manifest containing multi-edit settings.
 * @param configObject - section in config.json describing the table settings.
 * @param key - key or property in config.json.
 */
export function exportEnableMassEdit(manifestSection: unknown, configObject: unknown, key: string): void {
    const supportedProperties: string[] = ['visibleFields', 'ignoredFields', 'operationGroupingMode'];
    if (typeof configObject[key] === 'boolean') {
        manifestSection[key] = configObject[key];
    } else if (configObject[key] !== undefined) {
        const massEditTransferObject = typeof manifestSection[key] === 'object' ? { ...manifestSection[key] } : {};
        for (const propertyName of supportedProperties) {
            let exportValue: unknown;
            if (Array.isArray(configObject[key][propertyName]) && configObject[key][propertyName].length > 0) {
                // convert to the string format that is supported by manifest
                exportValue = configObject[key][propertyName].join(',');
            } else if (!Array.isArray(configObject[key][propertyName]) && configObject[key][propertyName]) {
                // for fields stored as simple values
                exportValue = configObject[key][propertyName];
            }

            if (exportValue !== undefined) {
                // Update with export value
                massEditTransferObject[propertyName] = exportValue;
            } else if (propertyName in massEditTransferObject) {
                // Property was deleted from configuration - remove in manifest
                delete massEditTransferObject[propertyName];
            }
        }
        manifestSection[key] = massEditTransferObject;
    }
}

/**
 * Sync class for PersonalizationType.
 */
export class PersonalizationType implements v4.PersonalizationType {
    //column
    @syncRule({
        manifest: {
            path: getTableSettingsPathForLR, // Here we are using path of parent to have correct value for manifestSection
            export: exportPersonalizationProperty,
            delete: deletePersonalizationProperty
        }
    })
    column?: boolean;

    //sort
    @syncRule({
        manifest: {
            path: getTableSettingsPathForLR,
            export: exportPersonalizationProperty,
            delete: deletePersonalizationProperty
        }
    })
    sort?: boolean;

    //filter
    @syncRule({
        manifest: {
            path: getTableSettingsPathForLR,
            export: exportPersonalizationProperty,
            delete: deletePersonalizationProperty
        }
    })
    filter?: boolean;
}

export class ViewPersonalizationType implements v4.ViewPersonalizationType {
    //column
    @syncRule({
        manifest: {
            path: getTableSettingsPathForView, // Here we are using path of parent to have correct value for manifestSection
            export: exportPersonalizationProperty,
            delete: deletePersonalizationProperty
        }
    })
    column?: boolean;

    //sort
    @syncRule({
        manifest: {
            path: getTableSettingsPathForView,
            export: exportPersonalizationProperty,
            delete: deletePersonalizationProperty
        }
    })
    sort?: boolean;

    //filter
    @syncRule({
        manifest: {
            path: getTableSettingsPathForLR,
            export: exportPersonalizationProperty,
            delete: deletePersonalizationProperty
        }
    })
    filter?: boolean;
}

/****************************************
 * Sync class for QuickVariantSelectionV4
 */

export class QuickVariantSelectionV4 implements v4.QuickVariantSelectionV4 {
    //paths
    @syncRule({
        manifest: {
            path: getQuickVariantPath
        }
    })
    paths: v4.AnnotationPathAsObject[];

    //hideTableTitle
    @syncRule({
        manifest: {
            path: getQuickVariantPath
        }
    })
    hideTableTitle?: boolean;

    //showCounts
    @syncRule({
        manifest: {
            path: getQuickVariantPath
        }
    })
    showCounts?: boolean;
}

/****************************************
 * Sync class for TableSettings
 */

export class TableSettings implements v4.TableSettings {
    //type
    @syncRule({
        manifest: {
            path: getTableSettingsPathForLR
        }
    })
    @validity({
        since: '1.84.0'
    })
    @descriptionSrcURL('https://ui5.sap.com/#/topic/7f844f1021cd4791b8f7408eac7c1cec')
    type?: v4.TableTypeV4;

    //personalization
    @syncRule({
        manifest: {
            path: getTableSettingsPathForLR,
            export: exportPersonalization
        }
    })
    @validity({
        since: '1.86.0'
    })
    personalization?: PersonalizationType | boolean;

    //selectionMode
    @syncRule({
        manifest: {
            path: getTableSettingsPathForLR
        }
    })
    @validity({
        since: '1.84.0'
    })
    @descriptionSrcURL('https://ui5.sap.com/sdk/#/topic/116b5d82e8c545e2a56e1b51b8b0a9bd')
    selectionMode?: v4.SelectionMode;

    //selectAll
    @syncRule({
        manifest: {
            path: getTableSettingsPathForLR
        }
    })
    @validity({
        since: '1.84.0'
    })
    selectAll?: boolean;

    //selectionLimit
    @syncRule({
        manifest: {
            path: getTableSettingsPathForLR
        }
    })
    @validity({
        since: '1.84.0'
    })
    selectionLimit?: number;

    //enableExport
    @syncRule({
        manifest: {
            path: getTableSettingsPathForLR
        }
    })
    @validity({
        since: '1.84.0'
    })
    @descriptionSrcURL('https://ui5.sap.com/sdk/#/topic/4bab6f2043814257974b52d4dafe1dcd')
    enableExport?: boolean;

    //quickVariantSelection
    @syncRule({
        manifest: {
            path: getTableSettingsPathForLR
        }
    })
    @validity({
        since: '1.84.0'
    })
    quickVariantSelection?: QuickVariantSelectionV4;

    //condensedTableLayout
    @syncRule({
        manifest: {
            path: getTableSettingsPathForLR
        }
    })
    @validity({
        since: '1.84.0'
    })
    @descriptionSrcURL('https://ui5.sap.com/sdk/#/topic/f3cc057e405c4fd58ee2ed42c557797c')
    condensedTableLayout?: boolean;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForLR
        }
    })
    @validity({
        since: '1.117.0'
    })
    @descriptionSrcURL('https://ui5.sap.com/#/topic/7f844f1021cd4791b8f7408eac7c1cec')
    hierarchyQualifier?: string;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForLR
        }
    })
    @validity({
        since: '1.120.0'
    })
    @descriptionSrcURL('https://ui5.sap.com/#/topic/c0f6592a592e47f9bb6d09900de47412')
    widthIncludingColumnHeader?: boolean;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForLR
        }
    })
    @validity({
        since: '1.123.0'
    })
    @descriptionSrcURL('https://ui5.sap.com/#/topic/c0f6592a592e47f9bb6d09900de47412')
    disableCopyToClipboard?: boolean;
}

/****************************************
 * Sync class for ALPChartView (renamed due to indirection in app schema)
 */

export class ALPChart implements v4.ALPChartView {
    //annotationPath
    @syncRule({
        manifest: {
            path: (pageName): string => getSettingsPathForPage(pageName) + '/views/paths/0/primary/0',
            delete: deleteView
        }
    })
    annotationPath: string;

    //toolBar
    toolBar?: v4.ViewChartToolBar;
}

export class CommonViewSettings implements v4.CommonViewSettings {
    //key
    @syncRule({
        manifest: {
            path: getSettingsPathForLRViewsPath,
            delete: doNotDelete
        }
    })
    key: string;
    //index
    @syncRule({
        manifest: {
            path: getSettingsPathForLRViewsPaths,
            import: determineIndexOfView,
            export: (manifestSection, configObject): void => {
                checkViewOrder(manifestSection, configObject);
            },
            delete: deleteView
        }
    })
    index: number;

    //annotationPath
    @syncRule({
        manifest: {
            path: getSettingsPathForLRViewsPath,
            delete: doNotDelete
        }
    })
    annotationPath: string;

    //entitySet
    @syncRule({
        manifest: {
            path: getSettingsPathForLRViewsPath
        }
    })
    entitySet?: string;
}

/****************************************
 * Sync class for view table settings
 */

export class TableViewSettings extends CommonViewSettings implements v4.TableSettings {
    //type
    @syncRule({
        manifest: {
            path: getTableSettingsPathForView
        }
    })
    @validity({
        since: '1.84.0'
    })
    type?: v4.TableTypeV4;

    //personalization
    @syncRule({
        manifest: {
            path: getTableSettingsPathForView
        }
    })
    @validity({
        since: '1.86.0'
    })
    personalization?: ViewPersonalizationType | boolean;

    //selectionMode
    @syncRule({
        manifest: {
            path: getTableSettingsPathForView
        }
    })
    @validity({
        since: '1.84.0'
    })
    selectionMode?: v4.SelectionMode;

    //selectAll
    @syncRule({
        manifest: {
            path: getTableSettingsPathForView
        }
    })
    @validity({
        since: '1.84.0'
    })
    selectAll?: boolean;

    //selectionLimit
    @syncRule({
        manifest: {
            path: getTableSettingsPathForView
        }
    })
    @validity({
        since: '1.84.0'
    })
    selectionLimit?: number;

    //enableExport
    @syncRule({
        manifest: {
            path: getTableSettingsPathForView
        }
    })
    @validity({
        since: '1.84.0'
    })
    @descriptionSrcURL('https://ui5.sap.com/sdk/#/topic/4bab6f2043814257974b52d4dafe1dcd')
    enableExport?: boolean;

    //quickVariantSelection
    @syncRule({
        manifest: {
            path: getTableSettingsPathForView
        }
    })
    @validity({
        since: '1.84.0'
    })
    quickVariantSelection?: QuickVariantSelectionV4;

    //condensedTableLayout
    @syncRule({
        manifest: {
            path: getTableSettingsPathForView
        }
    })
    @validity({
        since: '1.84.0'
    })
    @descriptionSrcURL('https://ui5.sap.com/sdk/#/topic/f3cc057e405c4fd58ee2ed42c557797c')
    condensedTableLayout?: boolean;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForView
        }
    })
    @validity({
        since: '1.120.0'
    })
    @descriptionSrcURL('https://ui5.sap.com/#/topic/c0f6592a592e47f9bb6d09900de47412')
    widthIncludingColumnHeader?: boolean;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForView
        }
    })
    @validity({
        since: '1.117.0'
    })
    @descriptionSrcURL('https://ui5.sap.com/#/topic/7f844f1021cd4791b8f7408eac7c1cec')
    hierarchyQualifier?: string;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForView
        }
    })
    @validity({
        since: '1.123.0'
    })
    @descriptionSrcURL('https://ui5.sap.com/#/topic/c0f6592a592e47f9bb6d09900de47412')
    disableCopyToClipboard?: boolean;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForView,
            import: importEnableMassEdit,
            export: exportEnableMassEdit
        }
    })
    @validity({
        since: '1.126.0'
    })
    enableMassEdit?: v4.EnableMassEdit;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForView,
            import: importCreationMode
        }
    })
    @descriptionSrcURL('https://ui5.sap.com/#/topic/ceb9284b16f64f30865ce999dbd56064')
    @validity({
        since: '1.130.0'
    })
    @hide(
        or(
            and(when(tableContext.type, v4.TableTypeV4.AnalyticalTable), not(exists(tableContext.creationMode))),
            when(tableContext.isInsertable, false)
        )
    )
    // The properties messages will be evaluated on the children level and will not be decorated to avoid duplicate messages.
    creationMode?: TableCreationModeLR;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForView
        }
    })
    @validity({
        since: '1.136.0'
    })
    @hide(
        and(not(tableContext.type, v4.TableTypeV4.AnalyticalTable), not(exists(tableContext.analyticalConfiguration)))
    )
    // The properties messages will be evaluated on the children level and will not be decorated to avoid duplicate messages.
    analyticalConfiguration?: ViewAnalyticalConfiguration;
}

/****************************************
 * Sync class for ALPTableView (renamed due to indirection in app schema)
 */

export class ALPTable extends TableViewSettings implements v4.ALPTableView {
    //annotationPath
    @syncRule({
        manifest: {
            path: (pageName): string => getSettingsPathForPage(pageName) + '/views/paths/0/secondary/0',
            delete: doNotDelete
        }
    })
    declare annotationPath: string;

    //initialLoad
    @syncRule({
        manifest: {
            path: (pageName: string[]): string => getSettingsPathForPage(pageName)
        }
    })
    @validity({
        since: '1.84.0'
    })
    @descriptionSrcURL('https://ui5.sap.com/#/topic/9f4e1192f1384b85bc160288e17f69c4')
    initialLoad?: v4.InitialLoadType;

    //toolBar
    toolBar?: ToolBar;
    columns?: v4.ViewColumns;
}

/****************************************
 * Sync class for LRChartView
 */

export class LRChartView extends CommonViewSettings implements v4.LRChartView {
    //toolBar
    toolBar?: v4.ViewChartToolBar;

    @syncRule({
        manifest: {
            path: getSettingsPathForLRViewsPath
        },
        generate: addPatternForBindingChangeOfEnumLR
    })
    visible?: boolean;
}

/****************************************
 * Sync class for LRTableView
 */

export class LRTableView extends TableViewSettings {
    //keepPreviousPersonalization
    @syncRule({
        manifest: {
            path: getSettingsPathForLRViewsPath
        }
    })
    keepPreviousPersonalization?: boolean;

    @syncRule({
        manifest: {
            path: getSettingsPathForLRViewsPath
        },
        generate: addPatternForBindingChangeOfEnumLR
    })
    visible?: boolean;

    //toolBar
    toolBar?: ToolBar;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForView
        }
    })
    @validity({
        since: '1.136.0'
    })
    @hide(
        and(not(tableContext.type, v4.TableTypeV4.AnalyticalTable), not(exists(tableContext.analyticalConfiguration)))
    )
    // The properties messages will be evaluated on the children level and will not be decorated to avoid duplicate messages.
    analyticalConfiguration?: ViewAnalyticalConfiguration;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForView
        }
    })
    @validity({
        since: '1.139.0'
    })
    @hide(not(tableContext.type, v4.TableTypeV4.ResponsiveTable))
    @message(
        and(not(tableContext.type, v4.TableTypeV4.ResponsiveTable), exists(tableContext.popinLayout)),
        msg(
            'PROPERTY_NOT_ALLOWED_FOR_TABLE_TYPE',
            { propertyName: 'popinLayout', tableType: tableContext.type },
            { deletable: true }
        )
    )
    popinLayout?: PopinLayout;
}

export class TableViewExtension implements v4.TableViewExtension {
    @syncRule({
        manifest: {
            path: getSettingsPathForLRViewsPath
        }
    })
    key: string;

    @syncRule({
        manifest: {
            path: getSettingsPathForLRViewsPaths,
            import: determineIndexOfView,
            export: (manifestSection, configObject): void => {
                checkViewOrder(manifestSection, configObject);
            },
            delete: deleteView
        }
    })
    index: number;

    @syncRule({
        manifest: {
            path: getSettingsPathForLRViewsPath
        }
    })
    label?: string;

    @syncRule({
        manifest: {
            path: getSettingsPathForLRViewsPath
        }
    })
    template: string;

    @syncRule({
        manifest: {
            path: getSettingsPathForLRViewsPath
        },
        generate: addPatternForBindingChangeOfEnumLR
    })
    visible?: boolean;
}

export class AnalyticalConfiguration implements v4.AnalyticalConfiguration {
    @syncRule({
        manifest: {
            path: getTableAnalyticalConfigurationPathForLR
        }
    })
    @validity({
        since: '1.136.0'
    })
    @hide(not(tableContext.type, v4.TableTypeV4.AnalyticalTable))
    @message(
        and(
            not(tableContext.type, v4.TableTypeV4.AnalyticalTable),
            exists(tableContext.analyticalConfiguration.aggregationOnLeafLevel)
        ),
        msg(
            'PROPERTY_NOT_ALLOWED_FOR_TABLE_TYPE',
            { propertyName: 'analyticalConfiguration', tableType: tableContext.type },
            { deletable: true }
        )
    )
    aggregationOnLeafLevel?: boolean;
}

export class ViewAnalyticalConfiguration implements v4.ViewAnalyticalConfiguration {
    @syncRule({
        manifest: {
            path: getTableAnalyticalConfigurationForView
        }
    })
    @validity({
        since: '1.136.0'
    })
    @hide(not(tableContext.type, v4.TableTypeV4.AnalyticalTable))
    @message(
        and(
            not(tableContext.type, v4.TableTypeV4.AnalyticalTable),
            exists(tableContext.analyticalConfiguration.aggregationOnLeafLevel)
        ),
        msg(
            'PROPERTY_NOT_ALLOWED_FOR_TABLE_TYPE',
            { propertyName: 'analyticalConfiguration', tableType: tableContext.type },
            { deletable: true }
        )
    )
    aggregationOnLeafLevel?: boolean;
}

/**
 * LR multi-view: own class for properties on views' level
 */
export class MultiTableModeV4 implements v4.MultiTableModeV4 {
    // List of annotation paths
    [key: string]: v4.ViewTypeV4;

    //showCounts
    @syncRule({
        manifest: {
            path: (pageName): string => getSettingsPathForPage(pageName) + '/views'
        }
    })
    showCounts?: boolean;
}

/****************************************
 * Sync class for Table
 */

export class Table extends TableSettings implements v4.Table {
    //annotationPath
    @syncRule({
        manifest: {
            path: (pageName): string => getSettingsPathForPage(pageName) + '/views/paths/0/secondary/0',
            delete: doNotDelete
        }
    })
    annotationPath?: string;

    //views
    views?: MultiTableModeV4;

    //initialLoad
    @syncRule({
        manifest: {
            path: (pageName: string[]): string => getSettingsPathForPage(pageName)
        }
    })
    @validity({
        since: '1.84.0'
    })
    @descriptionSrcURL('https://ui5.sap.com/#/topic/9f4e1192f1384b85bc160288e17f69c4')
    initialLoad?: v4.InitialLoadType;

    //toolBar
    toolBar?: ToolBar;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForLR,
            import: importEnableMassEdit,
            export: exportEnableMassEdit
        }
    })
    @validity({
        since: '1.126.0'
    })
    enableMassEdit?: v4.EnableMassEdit;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForLR,
            import: importCreationMode
        }
    })
    @descriptionSrcURL('https://ui5.sap.com/#/topic/ceb9284b16f64f30865ce999dbd56064')
    @validity({
        since: '1.130.0'
    })
    @hide(
        or(
            and(when(tableContext.type, v4.TableTypeV4.AnalyticalTable), not(exists(tableContext.creationMode))),
            when(tableContext.isInsertable, false)
        )
    )
    // The properties messages will be evaluated on the children level and will not be decorated to avoid duplicate messages.
    creationMode?: TableCreationModeLR;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForLR
        }
    })
    @validity({
        since: '1.136.0'
    })
    @hide(
        and(not(tableContext.type, v4.TableTypeV4.AnalyticalTable), not(exists(tableContext.analyticalConfiguration)))
    )
    // The properties messages will be evaluated on the children level and will not be decorated to avoid duplicate messages.
    analyticalConfiguration?: AnalyticalConfiguration;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForLR
        }
    })
    @validity({
        since: '1.139.0'
    })
    @hide(not(tableContext.type, v4.TableTypeV4.ResponsiveTable))
    @message(
        and(not(tableContext.type, v4.TableTypeV4.ResponsiveTable), exists(tableContext.popinLayout)),
        msg(
            'PROPERTY_NOT_ALLOWED_FOR_TABLE_TYPE',
            { propertyName: 'popinLayout', tableType: tableContext.type },
            { deletable: true }
        )
    )
    popinLayout?: PopinLayout;
}

export class TableCreationModeLR implements v4.TableCreationModeLR {
    @syncRule({
        manifest: {
            path: getTableSettingsPathForLR,
            export: exportCreationModeProperty,
            delete: deleteCreationModeProperty
        }
    })
    @descriptionSrcURL('https://ui5.sap.com/#/topic/ceb9284b16f64f30865ce999dbd56064')
    @hide(when(tableContext.type, v4.TableTypeV4.AnalyticalTable))
    @message(
        and(when(tableContext.type, v4.TableTypeV4.AnalyticalTable), exists(tableContext.creationMode.name)),
        msg(
            'PROPERTY_NOT_ALLOWED_FOR_TABLE_TYPE',
            { propertyName: 'creationMode', tableType: tableContext.type },
            { deletable: true }
        )
    )
    name?: v4.TableCreationModeType.NewPage | v4.TableCreationModeType.CreationDialog;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForLR,
            export: exportCreationModeCreationFieldsProperty,
            import: importCreationModeCreationFieldsProperty,
            delete: deleteCreationModeProperty
        }
    })
    @descriptionSrcURL('https://ui5.sap.com/#/topic/7fee938d7cba4bfd86810cfe6d011eb0')
    @hide(
        or(
            when(tableContext.type, v4.TableTypeV4.AnalyticalTable),
            not(tableContext.creationMode.name, v4.TableCreationModeType.CreationDialog)
        )
    )
    @message(
        and(when(tableContext.type, v4.TableTypeV4.AnalyticalTable), exists(tableContext.creationMode.creationFields)),
        msg(
            'PROPERTY_NOT_ALLOWED_FOR_TABLE_TYPE',
            { propertyName: 'creationFields', tableType: tableContext.type },
            { deletable: true }
        )
    )
    @message(
        and(
            not(tableContext.creationMode.name, v4.TableCreationModeType.CreationDialog),
            exists(tableContext.creationMode.creationFields)
        ),
        msg(
            'PROPERTY_ONLY_ALLOWED_FOR_CREATION_MODE',
            { propertyName: 'creationFields', creationMode: v4.TableCreationModeType.CreationDialog },
            { deletable: true }
        )
    )
    creationFields?: string[];

    @syncRule({
        manifest: {
            path: getTableSettingsPathForLR,
            export: exportCreationModeProperty,
            delete: deleteCreationModeProperty
        }
    })
    @descriptionSrcURL('https://ui5.sap.com/#/topic/7cf7a31fd1ee490ab816ecd941bd2f1f')
    @hide(not(tableContext.type, v4.TableTypeV4.TreeTable))
    @message(
        and(not(tableContext.type, v4.TableTypeV4.TreeTable), exists(tableContext.creationMode.createInPlace)),
        msg(
            'PROPERTY_ONLY_ALLOWED_FOR_TABLE_TYPE',
            { propertyName: 'createInPlace', tableType: v4.TableTypeV4.TreeTable },
            { deletable: true }
        )
    )
    createInPlace?: boolean;

    @syncRule({
        manifest: {
            path: getTableSettingsPathForLR,
            export: exportCreationModeProperty,
            delete: deleteCreationModeProperty
        }
    })
    @descriptionSrcURL('https://ui5.sap.com/#/topic/7cf7a31fd1ee490ab816ecd941bd2f1f')
    @hide(not(tableContext.type, v4.TableTypeV4.TreeTable))
    @message(
        and(not(tableContext.type, v4.TableTypeV4.TreeTable), exists(tableContext.creationMode.isCreateEnabled)),
        msg(
            'PROPERTY_ONLY_ALLOWED_FOR_TABLE_TYPE',
            { propertyName: 'isCreateEnabled', tableType: v4.TableTypeV4.TreeTable },
            { deletable: true }
        )
    )
    isCreateEnabled?: string;
}
